import React, { useState } from "react";

import axios from "axios";

import { toast } from "sonner";

import {
  ArrowRight,
  MessageCircle,
  Loader2,
  CheckCircle2,
} from "lucide-react";

import { Input } from "@/components/ui/input";

import { Label } from "@/components/ui/label";

import { Textarea } from "@/components/ui/textarea";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { WHATSAPP_URL } from "@/lib/brand";

const BUSINESS_TYPES = [
  "Clinic / Doctor",
  "Dental Clinic",
  "Diagnostic Lab",
  "Gym / Fitness",
  "Salon / Spa",
  "Restaurant / Cafe",
  "Coaching / Education",
  "Real Estate",
  "Retail Store",
  "Startup / Agency",
  "Other Local Business",
];

export default function LeadForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    business_type: "",
    plan_interest: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  const [submitted, setSubmitted] = useState(false);

  const onChange = (k, v) =>
    setForm((f) => ({
      ...f,
      [k]: v,
    }));

  const onSubmit = async (e) => {
    e.preventDefault();

    if (
      !form.name.trim() ||
      !form.phone.trim() ||
      !form.business_type
    ) {
      toast.error(
        "Please fill your name, phone and business type."
      );

      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...form,
        plan_interest: form.plan_interest || null,
        message: form.message || null,
      };

      await axios.post("/api/leads", payload);

      const msg = encodeURIComponent(
        `🚀 New GrowFlow Lead

Name: ${form.name}

Phone: ${form.phone}

Business Type: ${form.business_type}

Interested Plan: ${
          form.plan_interest || "Not specified"
        }

Message: ${form.message || "N/A"}`
      );

      window.location.href = `https://wa.me/919975247407?text=${msg}`;

      toast.success(
        "Thanks! We'll contact you shortly on WhatsApp."
      );

      setSubmitted(true);

      setForm({
        name: "",
        phone: "",
        business_type: "",
        plan_interest: "",
        message: "",
      });
    } catch (err) {
      console.error(err);

      toast.error(
        "Could not submit form. Please contact us on WhatsApp."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="lead-form"
      data-testid="lead-form-section"
      className="py-20 lg:py-28 bg-gradient-to-b from-white to-slate-50"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-14 items-start">

        {/* LEFT SIDE */}
        <div className="lg:col-span-5">

          <p className="text-xs uppercase tracking-[0.22em] font-bold text-[var(--gf-blue)]">
            Free Consultation
          </p>

          <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08]">
            Let's grow your business online.
          </h2>

          <p className="mt-5 text-lg text-[var(--gf-text-2)] leading-relaxed">
            Get a free consultation and discover how GrowFlow can
            help your business generate more calls, bookings and
            customer inquiries online.
          </p>

          {/* Benefits */}
          <div className="mt-8 space-y-4">

            {[
              "Free business consultation",
              "WhatsApp support",
              "SEO & mobile optimized websites",
              "Fast project delivery",
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3"
              >
                <div className="h-6 w-6 rounded-full bg-[var(--gf-mint)] flex items-center justify-center">
                  <CheckCircle2 className="h-4 w-4 text-[var(--gf-green-deep)]" />
                </div>

                <p className="text-sm text-[var(--gf-secondary)]">
                  {item}
                </p>
              </div>
            ))}
          </div>

          {/* WhatsApp CTA */}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="lead-form-side-whatsapp"
            className="mt-8 inline-flex items-center gap-2 bg-[var(--gf-green)] hover:bg-[var(--gf-green-deep)] text-white font-semibold h-12 px-6 rounded-full shadow-lg transition-all hover:-translate-y-0.5"
          >
            <MessageCircle className="h-4 w-4" />

            Chat on WhatsApp
          </a>
        </div>

        {/* FORM */}
        <form
          onSubmit={onSubmit}
          data-testid="lead-form"
          className="lg:col-span-7 bg-white rounded-3xl border border-[var(--gf-border)] gf-shadow-soft p-6 sm:p-8 lg:p-10"
        >

          <div className="grid sm:grid-cols-2 gap-5">

            {/* Name */}
            <div>
              <Label htmlFor="lf-name">
                Full Name *
              </Label>

              <Input
                id="lf-name"
                placeholder="John Doe"
                value={form.name}
                onChange={(e) =>
                  onChange("name", e.target.value)
                }
                required
              />
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="lf-phone">
                Phone / WhatsApp *
              </Label>

              <Input
                id="lf-phone"
                type="tel"
                placeholder="+91 98765 43210"
                value={form.phone}
                onChange={(e) =>
                  onChange("phone", e.target.value)
                }
                required
              />
            </div>

            {/* Business */}
            <div>
              <Label htmlFor="lf-biz">
                Business Type *
              </Label>

              <Select
                value={form.business_type}
                onValueChange={(v) =>
                  onChange("business_type", v)
                }
              >
                <SelectTrigger id="lf-biz">
                  <SelectValue placeholder="Select your business" />
                </SelectTrigger>

                <SelectContent>
                  {BUSINESS_TYPES.map((b) => (
                    <SelectItem key={b} value={b}>
                      {b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Plan */}
            <div>
              <Label htmlFor="lf-plan">
                Interested Plan
              </Label>

              <Select
                value={form.plan_interest}
                onValueChange={(v) =>
                  onChange("plan_interest", v)
                }
              >
                <SelectTrigger id="lf-plan">
                  <SelectValue placeholder="Select a package" />
                </SelectTrigger>

                <SelectContent>

                  <SelectItem value="Starter Website">
                    Starter Website — ₹4,999
                  </SelectItem>

                  <SelectItem value="Business Growth">
                    Business Growth — ₹9,999
                  </SelectItem>

                  <SelectItem value="Monthly Growth">
                    Monthly Growth — ₹7,999/mo
                  </SelectItem>

                  <SelectItem value="Not sure">
                    Not sure yet
                  </SelectItem>

                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Message */}
          <div className="mt-5">
            <Label htmlFor="lf-msg">
              Tell us about your business
            </Label>

            <Textarea
              id="lf-msg"
              placeholder="Tell us about your services, goals or what kind of website you need..."
              value={form.message}
              onChange={(e) =>
                onChange("message", e.target.value)
              }
              rows={5}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="mt-8 w-full inline-flex justify-center items-center gap-2 bg-[var(--gf-primary)] hover:bg-slate-800 text-white font-semibold h-14 px-6 rounded-full transition-all hover:-translate-y-0.5"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                Get Free Consultation
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>

          {/* Success */}
          {submitted && (
            <p className="mt-4 text-sm text-[var(--gf-green-deep)] text-center">
              ✓ Submitted successfully. We'll contact you shortly.
            </p>
          )}

          {/* Privacy */}
          <p className="mt-4 text-xs text-[var(--gf-text-2)] text-center">
            Your information is safe with us. No spam.
          </p>

        </form>
      </div>
    </section>
  );
}
